import { MakerSubmitApplication } from "@faire/retailer-visitor-shared/events";
import { MakerAdPageApply } from "@faire/retailer-visitor-shared/events";
import { INBOUND_BRAND_CATEGORY_OPTIONS } from "@faire/retailer-visitor-shared/features/MakerApplication/MakerApplicationCategories";
import { getBrandSignupAutoAcceptedThreeLetterCodes } from "@faire/retailer-visitor-shared/features/MakerApplication/MakerApplicationCountries";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import {
  BRAND_SIGN_UP_VALIDATION_RULES,
  MAKER_SIGN_UP_BASED_IN_STORAGE_KEY,
  validateUrlWithErrors,
} from "@faire/retailer-visitor-shared/views/apply/MakerApplication/consts";
import {
  IInboundApplicationFormData,
  useMakerApplicationDataForm,
} from "@faire/retailer-visitor-shared/views/apply/MakerApplication/MakerSignUp/useMakerApplicationDataForm";
import { MAKER_APPLICATION_DEFAULT_FORM_DATA } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/MakerSignUp/useMakerApplicationDataForm";
import {
  useMakerSignUpFormContextStore,
  type MakerSignUpFormStore,
} from "@faire/retailer-visitor-shared/views/apply/MakerApplication/MakerSignUpFormStore";
import { useMakerSignUp } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/useMakerSignUp";
import { ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS } from "@faire/web--source/common/consts/ISO_TO_COUNTRY_NAME";
import {
  ErrorMessages,
  getErrorMessage,
} from "@faire/web--source/common/getErrorMessage";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { throttleIfPossible } from "@faire/web--source/common/globals/throttleIfPossible";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logAssert, logError } from "@faire/web--source/common/logging";
import { LocalStorage } from "@faire/web--source/common/Storage";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import submitBrandLead from "@faire/web-api--source/endpoints/www/api/brand-lead/post";
import { useMutation as useValidateEmailMutation } from "@faire/web-api--source/endpoints/www/api/brand-lead/validate-email/post-hooks";
import validateTemporaryPosLinkKey from "@faire/web-api--source/endpoints/www/api/brand-lead/validate-temporary-pos-link-key/temporaryPosLinkKey/get";
import { trackBrandLeadsInboundBrandApplyRedirect } from "@faire/web-api--source/events/brandLeads/actionCategoryOther/inboundBrandApplyRedirect";
import { trackBrandLeadsBrandsFormBusinessNameClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormBusinessName";
import { trackBrandLeadsBrandsFormBusinessWebsiteClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormBusinessWebsite";
import { trackBrandLeadsBrandsFormCountryClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormCountry";
import { trackBrandLeadsBrandsFormDesktopNavigationNextClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormDesktopNavigationNext";
import { trackBrandLeadsBrandsFormDesktopNavigationReturnClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormDesktopNavigationReturn";
import { trackBrandLeadsBrandsFormEmailAddressClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormEmailAddress";
import { trackBrandLeadsBrandsFormHearAboutFaireClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormHearAboutFaire";
import { trackBrandLeadsBrandsFormMobileNavigationSecondNextClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormMobileNavigationSecondNext";
import { trackBrandLeadsBrandsFormNumberOfStoresClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormNumberOfStores";
import { trackBrandLeadsBrandsFormProductTypeClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormProductType";
import { trackBrandLeadsBrandSignUpFromFaireMarketClick } from "@faire/web-api--source/events/brandLeads/click/brandSignUpFromFaireMarket";
import { IBrandLead } from "@faire/web-api--source/indigofair/data/IBrandLead";
import { ICreateBrandLeadRequest } from "@faire/web-api--source/indigofair/data/ICreateBrandLeadRequest";
import { ICreateBrandLeadResponse } from "@faire/web-api--source/indigofair/data/ICreateBrandLeadResponse";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { route as brandMarketsApplicationRoute } from "@faire/web-api--source/routes/www/apply/markets";
import { route as brandOnboardingRoute } from "@faire/web-api--source/routes/www/brand-portal/brand-onboarding";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import isUndefined from "lodash/isUndefined";
import keys from "lodash/keys";
import mapValues from "lodash/mapValues";
import once from "lodash/once";
import { useCallback, useMemo, useState } from "react";
import { useHistory, matchPath } from "react-router-dom";
import validate from "validate.js";

export enum MakerApplicationStages {
  LANDING,
  FORM,
  ADDITIONAL_QUESTIONS,
  WAITLIST,
  UNSUPPORTED_AREA,
}

type MakerSignUpFormFields = Pick<
  MakerSignUpFormStore,
  | "_source"
  | "url"
  | "email"
  | "companyName"
  | "categoryOption"
  | "secondaryCategoryOptions"
  | "country"
  | "numProducts"
  | "stockists"
>;

export interface ICountry {
  isoCode: keyof typeof ISO3166Alpha3 | "Other" | "";
  displayName: string;
}

const submitEvent = once((isMobile: boolean) => {
  if (isMobile) {
    trackBrandLeadsBrandsFormMobileNavigationSecondNextClick();
  } else {
    trackBrandLeadsBrandsFormDesktopNavigationNextClick();
  }
});

const websiteUrlEvent = once(() =>
  trackBrandLeadsBrandsFormBusinessWebsiteClick()
);

const brandNameEvent = once(() => trackBrandLeadsBrandsFormBusinessNameClick());

const emailEvent = once(() => trackBrandLeadsBrandsFormEmailAddressClick());

const categoryEvent = once(() => trackBrandLeadsBrandsFormProductTypeClick());

const returnEvent = once(() =>
  trackBrandLeadsBrandsFormDesktopNavigationReturnClick()
);

const sourceEvent = once(() => trackBrandLeadsBrandsFormHearAboutFaireClick());

const countryEvent = once(() => trackBrandLeadsBrandsFormCountryClick());

const numberStoresEvent = once(() =>
  trackBrandLeadsBrandsFormNumberOfStoresClick()
);

const getFieldName = (apiFieldName: string) => {
  switch (apiFieldName) {
    case "phone_number":
      return "phone";
    case "email_address":
      return "email";
    default:
      return apiFieldName;
  }
};

export const useMakerSignUpForm = () => {
  const [
    {
      url,
      urlValidationInProgress,
      email,
      temporaryPosLinkKey,
      formErrors,
      apiErrors,
      companyName,
      categoryOption,
      secondaryCategoryOptions,
      country,
      numProducts,
      stockists,
      brandLeadToken,
      canProvideMoreInfo,
    },
    setMakerFormState,
  ] = useMakerSignUpFormContextStore([
    "url",
    "urlValidationInProgress",
    "email",
    "temporaryPosLinkKey",
    "formErrors",
    "apiErrors",
    "companyName",
    "categoryOption",
    "secondaryCategoryOptions",
    "country",
    "numProducts",
    "stockists",
    "brandLeadToken",
    "canProvideMoreInfo",
  ]);
  const {
    referringBrand,
    referringRetailer,
    source,
    isAd,
    setApplicationStage,
  } = useMakerSignUp();
  const [defaultData, setDefaultData] = useState<
    IInboundApplicationFormData | undefined
  >();
  const {
    getFormData: getApplicationFormData,
    setData: setApplicationFormData,
    clearData: clearApplicationFormData,
  } = useMakerApplicationDataForm(defaultData);
  const { retailerCountry } = useNationality();
  const { strictLocalize } = useStrictLocalization();
  const { push } = useHistory();
  const { isMobile } = useViewport();

  const { data: validateEmailData, mutateAsync: validateEmailAsync } =
    useValidateEmailMutation();

  const initializeFormData = useCallback(() => {
    const formData = getApplicationFormData();
    if (formData) {
      setMakerFormState({
        url: formData.url ?? "",
        email: formData.email ?? "",
        companyName: formData.companyName ?? "",
        categoryOption: formData.categoryOption,
        secondaryCategoryOptions: new Set(),
        country: formData.country ?? { isoCode: "", displayName: "" },
        numProducts: formData.numProducts,
        stockists: formData.stockists,
      });
    } else {
      setDefaultData(MAKER_APPLICATION_DEFAULT_FORM_DATA);
    }
  }, [getApplicationFormData, setMakerFormState]);

  const getCountryDefaultValue = () => {
    const retailerIso = retailerCountry ?? "";
    if (getBrandSignupAutoAcceptedThreeLetterCodes().includes(retailerIso)) {
      return {
        isoCode: retailerIso,
        displayName:
          // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
          ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS(strictLocalize)[retailerIso],
      };
    } else {
      return {
        isoCode: retailerIso,
        displayName: strictLocalize({
          defaultMessage: "Other",
          description: {
            text: "The option 'other' in a dropdown selection for countries.",
          },
        }),
      };
    }
  };

  const throttledSetApplicationFormData = useMemo(
    () =>
      throttleIfPossible(
        (formData: IInboundApplicationFormData) =>
          setApplicationFormData(formData),
        1000
      ),
    [setApplicationFormData]
  );

  const updateFormDataLocalStorage = useCallback(
    (newValues?: Partial<MakerSignUpFormFields>) => {
      const formData =
        getApplicationFormData() ?? MAKER_APPLICATION_DEFAULT_FORM_DATA;

      const currentValues = {
        url,
        email,
        companyName,
        categoryOption,
        country,
        numProducts,
        stockists,
      };

      Object.assign(formData, currentValues, newValues);

      if (country) {
        formData.country = country;
      }

      if (newValues?.country) {
        formData.country = newValues.country;
      }

      throttledSetApplicationFormData(formData);
    },
    [
      getApplicationFormData,
      url,
      email,
      companyName,
      categoryOption,
      country,
      numProducts,
      stockists,
      throttledSetApplicationFormData,
    ]
  );

  const validateUrl = useCallback(async () => {
    setMakerFormState((prev) => {
      const newFormErrors = { ...prev.formErrors };
      delete newFormErrors.url;

      return {
        formErrors: newFormErrors,
        urlValidationInProgress: true,
      };
    });

    const result = await validateUrlWithErrors(url, strictLocalize);

    if (result) {
      setMakerFormState((prev) => {
        const newFormErrors = { ...prev.formErrors };
        newFormErrors.url = result;

        return {
          formErrors: newFormErrors,
          urlValidationInProgress: false,
        };
      });

      return false;
    }

    setMakerFormState({
      urlValidationInProgress: false,
    });

    return true;
  }, [url, setMakerFormState, strictLocalize]);

  const validateEmailAddress = useCallback(async () => {
    setMakerFormState((prev) => {
      const newFormErrors = { ...prev.formErrors };
      delete newFormErrors.email;

      return {
        formErrors: newFormErrors,
      };
    });

    await validateEmailAsync([{ email_address: email }]);

    if (validateEmailData && !validateEmailData.is_valid) {
      setMakerFormState((prev) => ({
        formErrors: {
          ...prev.formErrors,
          email: strictLocalize({
            defaultMessage: "Please enter a valid email address",
          }),
        },
      }));

      return false;
    }

    return true;
  }, [
    email,
    validateEmailData,
    setMakerFormState,
    strictLocalize,
    validateEmailAsync,
  ]);

  const isUrlValid = useMemo(
    () => url && !urlValidationInProgress && (!formErrors || !formErrors.url),
    [url, urlValidationInProgress, formErrors]
  );

  const setFormField = useCallback(
    (
      fieldName: keyof MakerSignUpFormFields,
      value: MakerSignUpFormFields[keyof MakerSignUpFormFields]
    ) => {
      setMakerFormState((prev) => {
        const newFormErrors = { ...prev.formErrors };
        delete newFormErrors[fieldName];

        return {
          formErrors: newFormErrors,
          [fieldName]: value,
        };
      });

      updateFormDataLocalStorage({
        [fieldName]: value,
      });
    },
    [setMakerFormState, updateFormDataLocalStorage]
  );

  const errorFor = (property: string) => {
    return formErrors[property] || apiErrors[property];
  };

  const setCountry = useCallback(
    (country: ICountry) => {
      setMakerFormState((prev) => {
        const newFormErrors = { ...prev.formErrors };
        delete newFormErrors["country.isoCode"];

        return {
          country,
          formErrors: newFormErrors,
        };
      });

      updateFormDataLocalStorage({
        country,
      });
    },
    [setMakerFormState, updateFormDataLocalStorage]
  );

  const countryLabel = country?.isoCode;

  const onUrlBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      websiteUrlEvent();
      setFormField("url", event.target.value);
    },
    [setFormField]
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormField(
        event.target.name as keyof MakerSignUpFormFields,
        event.target.value
      );
    },
    [setFormField]
  );

  const selectedProductCategory = useMemo(() => {
    return INBOUND_BRAND_CATEGORY_OPTIONS(strictLocalize).find(
      (opt) => opt.value === categoryOption?.value
    );
  }, [strictLocalize, categoryOption]);

  const shouldCollectSecondaryProductCategory = useMemo(() => {
    const stockistsLessThan25 = !isUndefined(stockists) && stockists <= 25;
    const currentSelectedCategoryOption = selectedProductCategory;

    return (
      stockistsLessThan25 &&
      categoryOption?.value &&
      currentSelectedCategoryOption!.subCategories.length > 0
    );
  }, [stockists, categoryOption, selectedProductCategory]);

  const isFromFaireMarkets = useMemo(() => {
    return matchPath(removeLocaleFromUrl(getLocationOrThrow().pathname), {
      path: removeLocaleFromUrl(brandMarketsApplicationRoute),
      exact: true,
    });
  }, []);

  const handleValidateTemporaryPosLinkKey =
    useCallback(async (): Promise<boolean> => {
      if (temporaryPosLinkKey) {
        const response = await validateTemporaryPosLinkKey(temporaryPosLinkKey);
        switch (response.temporary_pos_link_key_validation_result) {
          case "ALREADY_LINKED":
            setMakerFormState((prev) => ({
              apiErrors: {
                ...prev.apiErrors,
                error: strictLocalize({
                  defaultMessage: "Shopify account already linked.",
                }),
              },
            }));
            return false;
          case "NO_POS_LINK_FOUND":
          case "EMPTY_REQUEST":
          case "TEMPORARY_POS_LINK_KEY_VALIDATION_RESULT_UNKNOWN":
            setMakerFormState((prev) => ({
              apiErrors: {
                ...prev.apiErrors,
                error: strictLocalize({
                  defaultMessage:
                    "Your link may have expired, please try again from Shopify.",
                }),
              },
            }));
            return false;
          default:
            return true;
        }
      }

      return true;
    }, [temporaryPosLinkKey, setMakerFormState, strictLocalize]);

  const validateForm = useCallback(async () => {
    const validateEmailAddressResult = await validateEmailAddress();
    const validateUrlResult = await validateUrl();

    if (!validateEmailAddressResult || !validateUrlResult) {
      return false;
    }

    const validationRules = BRAND_SIGN_UP_VALIDATION_RULES(strictLocalize);
    const validationObject: MakerSignUpFormFields = {
      url,
      email,
      companyName,
      categoryOption,
      secondaryCategoryOptions,
      country,
      numProducts,
      stockists,
    };
    let validationErrors = validate(validationObject, validationRules);

    if (typeof validationErrors === "undefined") {
      validationErrors = {};
    } else {
      // validate.js gives us back an array of errors for each value, just take the first one for each.
      validationErrors = mapValues(validationErrors, (value) => value[0]);
    }

    setMakerFormState((prev) => ({
      formErrors: {
        ...prev.formErrors,
        ...validationErrors,
      },
    }));

    return (
      validateEmailAddressResult &&
      validateUrlResult &&
      keys(validationErrors).length === 0
    );
  }, [
    categoryOption,
    companyName,
    country,
    email,
    numProducts,
    secondaryCategoryOptions,
    stockists,
    strictLocalize,
    url,
    validateEmailAddress,
    validateUrl,
    setMakerFormState,
  ]);

  const handleFormSubmit = useCallback(
    (response: ICreateBrandLeadResponse) => {
      const brandLead = response.brand_lead;
      logAssert(!!brandLead, "brandLead should always be defined");
      logAssert(!!brandLead.token, "brandLead.token should always be defined");

      setMakerFormState({
        brandLeadToken: brandLead.token,
      });

      if (isAd) {
        MakerAdPageApply.publish(brandLead);
      } else {
        MakerSubmitApplication.publish(brandLead);
      }

      if (isFromFaireMarkets) {
        trackBrandLeadsBrandSignUpFromFaireMarketClick();
      }

      LocalStorage.get().removeItem(MAKER_SIGN_UP_BASED_IN_STORAGE_KEY);

      setMakerFormState({
        canProvideMoreInfo: response.can_provide_more_info,
      });
      // If onboarding_params is present, then brand's application was accepted
      if (response?.onboarding_params) {
        // redirect to brand onboarding flow
        clearApplicationFormData();
        trackBrandLeadsInboundBrandApplyRedirect("onboarding");
        push(`${brandOnboardingRoute}?${response.onboarding_params}`);
      } else {
        if (response?.is_supported_country === false) {
          trackBrandLeadsInboundBrandApplyRedirect("unsupported-area");
          setApplicationStage(MakerApplicationStages.UNSUPPORTED_AREA);
        } else {
          trackBrandLeadsInboundBrandApplyRedirect("waitlist");
          setApplicationStage(MakerApplicationStages.WAITLIST);
        }
      }
    },
    [
      clearApplicationFormData,
      isAd,
      isFromFaireMarkets,
      push,
      setApplicationStage,
      setMakerFormState,
    ]
  );

  const onSubmit = useCallback(
    async (
      event:
        | React.MouseEvent<HTMLButtonElement>
        | React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.stopPropagation();
      event.preventDefault();

      const isTemporaryPosKeyValid = await handleValidateTemporaryPosLinkKey();
      if (!isTemporaryPosKeyValid) {
        return;
      }

      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      const categoryValue = categoryOption?.value;

      const payloadBase = {
        company_name: companyName,
        url: url,
        email_address: email,
        number_of_stockists: stockists,
        number_of_products: numProducts,
        referring_brand_token_alias: referringBrand?.token_alias,
        referring_retailer_token: referringRetailer?.token,
        source: source,
        primary_category: categoryValue,
        country: country?.isoCode,
        secondary_categories: shouldCollectSecondaryProductCategory
          ? [...(secondaryCategoryOptions || [])]
          : [],
      };

      const payload: IBrandLead = IBrandLead.build(payloadBase);

      submitEvent(isMobile);

      try {
        const result = await submitBrandLead(
          ICreateBrandLeadRequest.build({
            brand_lead: payload,
            term_of_service_accepted: true,
            temporary_pos_link_key: temporaryPosLinkKey,
          })
        );

        if (result.brand_lead) {
          handleFormSubmit(result);
        } else {
          setMakerFormState({ brandLeadCreationFailure: true });
        }
      } catch (submitError) {
        setMakerFormState({ brandLeadCreationFailure: true });
        logError(submitError);
        if (submitError instanceof WebApiError) {
          const fieldName = submitError.responseData.field
            ? getFieldName(submitError.responseData.field)
            : "error";

          const fieldError =
            submitError.responseData.localized_message ||
            submitError.responseData.message ||
            "";

          setMakerFormState((prev) => ({
            apiErrors: {
              ...prev.apiErrors,
              [fieldName]: fieldError,
            },
          }));
        } else {
          setMakerFormState((prev) => ({
            apiErrors: {
              ...prev.apiErrors,
              error: getErrorMessage(
                strictLocalize,
                ErrorMessages.PLEASE_TRY_AGAIN_OR_CONTACT_SUPPORT
              ),
            },
          }));
        }
      }
    },
    [
      handleValidateTemporaryPosLinkKey,
      validateForm,
      categoryOption?.value,
      companyName,
      url,
      email,
      stockists,
      numProducts,
      referringBrand?.token_alias,
      referringRetailer?.token,
      source,
      country?.isoCode,
      shouldCollectSecondaryProductCategory,
      secondaryCategoryOptions,
      isMobile,
      temporaryPosLinkKey,
      handleFormSubmit,
      setMakerFormState,
      strictLocalize,
    ]
  );

  const onClickTellUsMoreFunction: (() => void) | undefined = () => {
    if (!canProvideMoreInfo) {
      return undefined;
    } else {
      return () =>
        setApplicationStage(MakerApplicationStages.ADDITIONAL_QUESTIONS);
    }
  };

  const setEmailAddress = useCallback(
    (email: string) => {
      setMakerFormState({
        email: email,
      });
    },
    [setMakerFormState]
  );

  const setBrandLeadToken = useCallback(
    (brandLeadToken: string | null) => {
      setMakerFormState({
        brandLeadToken: brandLeadToken,
      });
    },
    [setMakerFormState]
  );

  const setTemporaryPosLinkKey = useCallback(
    (temporaryPosLinkKey: string | undefined) => {
      setMakerFormState({
        temporaryPosLinkKey: temporaryPosLinkKey,
      });
    },
    [setMakerFormState]
  );

  return {
    initializeFormData,
    onSubmit,
    getCountryDefaultValue,
    countryLabel,
    onUrlBlur,
    errorFor,
    isUrlValid,
    onChange,
    brandNameEvent,
    setCountry,
    emailEvent,
    categoryEvent,
    setFormField,
    returnEvent,
    sourceEvent,
    countryEvent,
    selectedProductCategory,
    shouldCollectSecondaryProductCategory,
    numberStoresEvent,
    email,
    setEmailAddress,
    brandLeadToken,
    setBrandLeadToken,
    setTemporaryPosLinkKey,
    canProvideMoreInfo,
    onClickTellUsMoreFunction,
  };
};
